import apiClient from './apiClient';

export interface Feedback {
  _id?: string;
  content: string;
  category: FeedbackCategory;
  status?: FeedbackStatus;
  adminNotes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FeedbackSubmissionResponse extends Feedback {
  rewardInfo?: {
    isFirstFeedback: boolean;
    rewardMinutes?: number;
    message?: string;
  };
}

export enum FeedbackCategory {
  GENERAL = 'general',
  INTERVIEW = 'interview',
  USABILITY = 'usability',
  FEATURE = 'feature',
  BUG = 'bug',
  EXPERIENCE = 'experience',
  REPORT = 'interview_report'
}

export enum InterviewFeedbackCategory {
  USABILITY = 'usability',
  EXPERIENCE = 'experience',
  REPORT = 'interview_report',
}

export enum FeedbackStatus {
  PENDING = 'pending',
  REVIEWED = 'reviewed',
  IMPLEMENTED = 'implemented',
  REJECTED = 'rejected'
}

// Helper function to extract data from API response similar to other services
function extractDataFromResponse<T>(response: any, entityName: string): T {
  console.log(`UserFeedbackService: ${entityName} raw response:`, response);
  
  try {
    // Case 1: If it directly matches our type, use it directly
    if (response && typeof response === 'object' && '_id' in response) {
      console.log(`UserFeedbackService: ${entityName} is already in expected format`);
      return response as T;
    }
    
    // Case 2: Handle normalized response from apiClient (numeric keys with _response metadata)
    if (response && typeof response === 'object' && '_response' in response) {
      console.log(`UserFeedbackService: ${entityName} is in normalized format with _response property`);
      
      // Check if the response itself is the data we're looking for
      if ('_id' in response) {
        const { _response, ...data } = response;
        return data as T;
      }
      
      // Collect the first item with numeric key
      for (const key in response) {
        // Skip non-numeric keys and _response
        if (key === '_response' || isNaN(Number(key))) {
          continue;
        }
        
        return response[key] as T;
      }
    }
    
    // Case 3: If it has a nested data property
    if (response && response.data) {
      console.log(`UserFeedbackService: ${entityName} extracted from response.data`);
      return response.data as T;
    }
    
    // Return the original response as a fallback
    return response as T;
  } catch (error) {
    console.error(`UserFeedbackService: Error processing ${entityName} response:`, error);
    throw error;
  }
}

// Helper function to extract array data from API response
function extractArrayDataFromResponse<T>(response: any, entityName: string): T[] {
  console.log(`UserFeedbackService: ${entityName} raw array response:`, response);
  
  try {
    // Case 1: If it's already an array, use it directly
    if (Array.isArray(response)) {
      console.log(`UserFeedbackService: ${entityName} is already an array with ${response.length} items`);
      return response as T[];
    }
    
    // Case 2: Handle normalized response from apiClient (numeric keys with _response metadata)
    if (response && typeof response === 'object' && '_response' in response) {
      console.log(`UserFeedbackService: ${entityName} is in normalized format with _response property`);
      
      // Collect all items with numeric keys
      const items: T[] = [];
      for (const key in response) {
        // Skip non-numeric keys and _response
        if (key === '_response' || isNaN(Number(key))) {
          continue;
        }
        
        items.push(response[key] as T);
      }
      
      console.log(`UserFeedbackService: ${entityName} extracted ${items.length} items from normalized response`);
      return items;
    }
    
    // Case 3: If it has a nested data property that's an array
    if (response && response.data && Array.isArray(response.data)) {
      console.log(`UserFeedbackService: ${entityName} extracted from response.data with ${response.data.length} items`);
      return response.data as T[];
    }
    
    // Case 4: If it's an object with a nested structure { statusCode, message, data }
    if (response && typeof response === 'object' && 'statusCode' in response && response.data) {
      if (Array.isArray(response.data)) {
        console.log(`UserFeedbackService: ${entityName} extracted from ApiResponse.data with ${response.data.length} items`);
        return response.data as T[];
      }
      
      // If response.data is an object with another data property (double nesting)
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log(`UserFeedbackService: ${entityName} extracted from double-nested data with ${response.data.data.length} items`);
        return response.data.data as T[];
      }
    }
    
    // Return empty array as fallback
    console.warn(`UserFeedbackService: Unexpected ${entityName} response format, returning empty array`);
    return [];
  } catch (error) {
    console.error(`UserFeedbackService: Error processing ${entityName} response:`, error);
    return [];
  }
}

class UserFeedbackService {
  private baseUrl = '/feedback';
  
  // Cache for feedback to prevent multiple API calls
  private feedbackCache: {
    myFeedback: Feedback[] | null;
    timestamp: number;
    pendingPromise: Promise<Feedback[]> | null;
  } = {
    myFeedback: null,
    timestamp: 0,
    pendingPromise: null
  };
  
  // Cache TTL in milliseconds (5 minutes)
  private cacheTTL = 5 * 60 * 1000;
  
  // Submit new feedback
  async submitFeedback(feedback: { content: string; category: FeedbackCategory }): Promise<FeedbackSubmissionResponse> {
    try {
      const response = await apiClient.post<FeedbackSubmissionResponse>(this.baseUrl, feedback);
      
      // Process the response to extract the feedback data
      const processedResponse = extractDataFromResponse<FeedbackSubmissionResponse>(response, 'feedback submission');
      
      // Clear the cache to ensure fresh data on next fetch
      this.clearCache();
      
      return processedResponse;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }
  
  // Get all feedback for the current user
  async getMyFeedback(forceRefresh = false): Promise<Feedback[]> {
    try {
      const now = Date.now();
      const cacheExpired = now - this.feedbackCache.timestamp > this.cacheTTL;
      
      // Return cached data if available and not expired
      if (this.feedbackCache.myFeedback && !cacheExpired && !forceRefresh) {
        console.log('UserFeedbackService: Using cached feedback data');
        return this.feedbackCache.myFeedback;
      }
      
      // If there's already a pending request, return that promise
      if (this.feedbackCache.pendingPromise && !forceRefresh) {
        console.log('UserFeedbackService: Returning pending feedback request');
        return this.feedbackCache.pendingPromise;
      }
      
      // Create a new request promise
      console.log('UserFeedbackService: Fetching user feedback from API');
      const fetchPromise = this.fetchMyFeedback();
      
      // Store the promise
      this.feedbackCache.pendingPromise = fetchPromise;
      
      // Process the response
      try {
        const data = await fetchPromise;
        
        // Store result in cache
        this.feedbackCache.myFeedback = data;
        this.feedbackCache.timestamp = Date.now();
        
        return data;
      } finally {
        // Clear the promise when done
        this.feedbackCache.pendingPromise = null;
      }
    } catch (error) {
      console.error('Error getting user feedback:', error);
      // Clear the pending promise on error
      this.feedbackCache.pendingPromise = null;
      throw error;
    }
  }
  
  // Fetch feedback from the API
  private async fetchMyFeedback(): Promise<Feedback[]> {
    try {
      const response = await apiClient.get<Feedback[]>(`${this.baseUrl}/my-feedback`);
      
      // Process the response to extract the feedback data
      return extractArrayDataFromResponse<Feedback>(response, 'user feedback');
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      return [];
    }
  }
  
  // Get specific feedback
  async getFeedback(id: string): Promise<Feedback> {
    try {
      const response = await apiClient.get<Feedback>(`${this.baseUrl}/${id}`);
      
      // Process the response to extract the feedback data
      return extractDataFromResponse<Feedback>(response, `feedback ${id}`);
    } catch (error) {
      console.error(`Error getting feedback with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Update feedback
  async updateFeedback(id: string, updates: Partial<Feedback>): Promise<Feedback> {
    try {
      const response = await apiClient.patch<Feedback>(`${this.baseUrl}/${id}`, updates);
      
      // Process the response to extract the feedback data
      const processedResponse = extractDataFromResponse<Feedback>(response, `updated feedback ${id}`);
      
      // Clear the cache to ensure fresh data on next fetch
      this.clearCache();
      
      return processedResponse;
    } catch (error) {
      console.error(`Error updating feedback with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Delete feedback
  async deleteFeedback(id: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete<{ message: string }>(`${this.baseUrl}/${id}`);
      
      // Clear the cache to ensure fresh data on next fetch
      this.clearCache();
      
      if (response && typeof response === 'object' && 'message' in response) {
        return response as { message: string };
      }
      
      return { message: 'Feedback deleted successfully' };
    } catch (error) {
      console.error(`Error deleting feedback with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Check if user has submitted feedback already
  async hasFeedback(): Promise<boolean> {
    try {
      const feedback = await this.getMyFeedback();
      return feedback.length > 0;
    } catch (error) {
      console.error('Error checking if user has feedback:', error);
      return false;
    }
  }
  
  // Clear the cache
  clearCache(): void {
    this.feedbackCache.myFeedback = null;
    this.feedbackCache.timestamp = 0;
    this.feedbackCache.pendingPromise = null;
    console.log('UserFeedbackService: Cache cleared');
  }
}

const userFeedbackService = new UserFeedbackService();
export default userFeedbackService; 